/**
 * Stream B — B38: UGC Library
 *
 * Gallery of user-generated content. Approval workflow.
 * Rights tracking. Real apiFetch. CSS variables only.
 */
import { useState, useEffect, useCallback, useRef, type FormEvent } from "react";
import { apiFetch } from "@/lib/api-client";
import {
  Image,
  Video,
  FileText,
  Check,
  X,
  Shield,
  ShieldCheck,
  ShieldQuestion,
  ShieldX,
  Eye,
  AlertCircle,
  Filter,
  PlusCircle,
} from "lucide-react";

interface UgcItem {
  id: string;
  platform: string;
  authorName: string;
  authorHandle: string;
  caption: string;
  mediaType: "image" | "video" | "text";
  mediaUrl: string | null;
  status: "pending" | "approved" | "rejected";
  rightsStatus: "not_requested" | "requested" | "granted" | "denied";
  engagements: number;
  createdAt: string;
}

interface UgcData {
  items: UgcItem[];
  total: number;
  pendingCount: number;
  approvedCount: number;
}

type CaptureMediaType = "image" | "video" | "text";

interface CaptureFormState {
  platform: string;
  authorName: string;
  authorHandle: string;
  caption: string;
  mediaType: CaptureMediaType;
  mediaUrl: string;
  sourcePostUrl: string;
  primaryMediaUploadId: string | null;
}

const INITIAL_CAPTURE_FORM: CaptureFormState = {
  platform: "first_party",
  authorName: "",
  authorHandle: "",
  caption: "",
  mediaType: "text",
  mediaUrl: "",
  sourcePostUrl: "",
  primaryMediaUploadId: null,
};

type SocialVideoFormat = "portrait" | "square" | "landscape";

const SOCIAL_VIDEO_FORMATS: Record<SocialVideoFormat, {
  label: string;
  description: string;
  aspectClass: string;
  fileSuffix: string;
}> = {
  portrait: {
    label: "Portrait 9:16",
    description: "TikTok, Reels, Shorts",
    aspectClass: "aspect-[9/16]",
    fileSuffix: "portrait",
  },
  square: {
    label: "Square 1:1",
    description: "Instagram feed, LinkedIn, Facebook",
    aspectClass: "aspect-square",
    fileSuffix: "square",
  },
  landscape: {
    label: "Landscape 16:9",
    description: "YouTube, landing-page embeds",
    aspectClass: "aspect-video",
    fileSuffix: "landscape",
  },
};

const TELEPROMPTER_PROMPTS = [
  {
    title: "Outcome story",
    lines: [
      "What problem did you have before working with us?",
      "What changed after the service or program?",
      "What would you tell someone considering booking?",
    ],
    caption: "Customer video review: problem, outcome, recommendation.",
  },
  {
    title: "Fast trust proof",
    lines: [
      "Say your name and what you booked.",
      "Share one specific result or moment.",
      "End with a short recommendation.",
    ],
    caption: "Short customer video review for social proof.",
  },
  {
    title: "Before and after",
    lines: [
      "Describe the before state in one sentence.",
      "Describe the after state in one sentence.",
      "Share the moment you knew it worked.",
    ],
    caption: "Before-and-after customer video testimonial.",
  },
] as const;

interface PageAssetUploadResponse {
  id: string;
  publicUrl?: string | null;
  publicPath?: string | null;
  fileUrl?: string | null;
}

interface VideoReviewUploadResult {
  assetId: string;
  assetUrl: string;
  suggestedCaption: string;
}

function createCaptureSourceId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  const random = Math.floor(Math.random() * 0xffffffffffff);
  return `00000000-0000-4000-8000-${random.toString(16).padStart(12, "0").slice(0, 12)}`;
}

function resolveAbsoluteUrl(pathOrUrl: string) {
  if (typeof window === "undefined") {
    return pathOrUrl;
  }

  return new URL(pathOrUrl, window.location.origin).toString();
}

function resolveUploadedAssetUrl(asset: PageAssetUploadResponse) {
  return resolveAbsoluteUrl(asset.fileUrl || asset.publicUrl || asset.publicPath || `/crm/pages/assets/${asset.id}/file`);
}

function chooseVideoMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return undefined;
  }

  const supported = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType));

  return supported;
}

const RIGHTS_CONFIG = {
  not_requested: { icon: Shield, style: "text-[var(--color-muted-foreground)] bg-[var(--color-surface)]", label: "Not Requested" },
  requested: { icon: ShieldQuestion, style: "text-[var(--color-warning)] bg-[var(--color-warning)]/10", label: "Requested" },
  granted: { icon: ShieldCheck, style: "text-[var(--color-success)] bg-[var(--color-success)]/10", label: "Granted" },
  denied: { icon: ShieldX, style: "text-[var(--color-error)] bg-[var(--color-error)]/10", label: "Denied" },
} as const;

function RightsBadge({ status }: { status: UgcItem["rightsStatus"] }) {
  const config = RIGHTS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${config.style}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

const STATUS_RING = {
  approved: "ring-[var(--color-success)]/50",
  rejected: "ring-[var(--color-error)]/50",
  pending: "ring-[var(--color-warning)]/50",
} as const;

function UgcCard({
  item,
  onAction,
  isActioning,
}: {
  item: UgcItem;
  onAction: (id: string, action: "approve" | "reject" | "request_rights") => void;
  isActioning: boolean;
}) {
  const MediaIcon = item.mediaType === "video" ? Video : item.mediaType === "image" ? Image : FileText;

  return (
    <div className={`group relative rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] overflow-hidden ring-2 ${STATUS_RING[item.status]}`}>
      <div className="aspect-square bg-[var(--color-surface)] relative flex items-center justify-center">
        <MediaIcon className="h-10 w-10 text-[var(--color-muted-foreground)]/50" />
        {item.mediaType === "video" && (
          <span className="absolute bottom-2 left-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
            Video
          </span>
        )}

        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {item.status === "pending" && (
            <>
              <button
                type="button"
                onClick={() => onAction(item.id, "approve")}
                disabled={isActioning}
                className="rounded-full bg-[var(--color-success)] p-2 text-white hover:opacity-90 disabled:opacity-50"
                title="Approve"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onAction(item.id, "reject")}
                disabled={isActioning}
                className="rounded-full bg-[var(--color-error)] p-2 text-white hover:opacity-90 disabled:opacity-50"
                title="Reject"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          )}
          {item.rightsStatus === "not_requested" && (
            <button
              type="button"
              onClick={() => onAction(item.id, "request_rights")}
              disabled={isActioning}
              className="rounded-full bg-[var(--color-primary)] p-2 text-white hover:opacity-90 disabled:opacity-50"
              title="Request Rights"
            >
              <Shield className="h-4 w-4" />
            </button>
          )}
          <button type="button" className="rounded-full bg-background/20 p-2 text-white hover:bg-background/30" title="Preview">
            <Eye className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-[var(--color-foreground)] truncate">{item.authorName}</span>
          <span className="text-xs text-[var(--color-muted-foreground)]">@{item.authorHandle}</span>
        </div>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)] line-clamp-2">{item.caption}</p>
        <div className="mt-2 flex items-center justify-between">
          <RightsBadge status={item.rightsStatus} />
          <span className="text-[10px] text-[var(--color-muted-foreground)]">{item.engagements} engagements</span>
        </div>
      </div>
    </div>
  );
}

const STATUS_TABS = [
  { value: undefined, label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

export default function UgcLibrary() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [data, setData] = useState<UgcData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [captureForm, setCaptureForm] = useState<CaptureFormState>(INITIAL_CAPTURE_FORM);
  const [captureSourceId, setCaptureSourceId] = useState(() => createCaptureSourceId());

  const fetchData = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const res = await apiFetch<{ data: UgcData }>(`/crm/social/ugc${params}`);
      setData(res.data);
      setError(false);
    } catch {
      setData(null);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (id: string, action: "approve" | "reject" | "request_rights") => {
    setIsActioning(true);
    try {
      await apiFetch(`/crm/social/ugc/${id}/${action}`, { method: "POST" });
      await fetchData();
    } catch {
      // Action failed — card stays for retry
    } finally {
      setIsActioning(false);
    }
  };

  const handleCaptureChange = <K extends keyof CaptureFormState>(field: K, value: CaptureFormState[K]) => {
    setCaptureForm((current) => ({ ...current, [field]: value }));
  };

  const handleCaptureSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setCaptureError(null);

    try {
      await apiFetch<{ data: { id: string } }>("/crm/social/ugc/collect", {
        method: "POST",
        body: JSON.stringify({
          sourceType: "manual",
          sourceId: captureSourceId,
          platform: captureForm.platform,
          authorName: captureForm.authorName,
          authorHandle: captureForm.authorHandle,
          caption: captureForm.caption,
          mediaType: captureForm.mediaType,
          mediaUrl: captureForm.mediaUrl || null,
          hashtags: [],
          engagements: 0,
          reach: 0,
          sourcePostUrl: captureForm.sourcePostUrl,
          primaryMediaUploadId: captureForm.primaryMediaUploadId,
        }),
      });
      setCaptureForm(INITIAL_CAPTURE_FORM);
      setCaptureSourceId(createCaptureSourceId());
      await fetchData();
    } catch {
      setCaptureError("Unable to save first-party content right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-[var(--color-surface)]" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <div key={i} className="aspect-square animate-pulse rounded-xl bg-[var(--color-surface)]" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load UGC library.
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <Image className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
          <p className="font-medium text-[var(--color-foreground)]">No user-generated content yet</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Capture first-party text, image, or video content here, or wait for social mentions to land.</p>
        </div>
        <form
          onSubmit={handleCaptureSubmit}
          className="h-fit rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
        >
          <div className="flex items-center gap-2">
            <PlusCircle className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Capture First-Party Review</h2>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Manual text/image capture plus browser-recorded video reviews with teleprompter prompts.
          </p>
          <VideoReviewRecorder
            onUploaded={(upload) => {
              setCaptureForm((current) => ({
                ...current,
                mediaType: "video",
                mediaUrl: upload.assetUrl,
                sourcePostUrl: upload.assetUrl,
                primaryMediaUploadId: upload.assetId,
                caption: current.caption.trim() ? current.caption : upload.suggestedCaption,
              }));
            }}
          />
          <CaptureFields
            form={captureForm}
            captureError={captureError}
            isSubmitting={isSubmitting}
            onChange={handleCaptureChange}
          />
        </form>
      </div>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">UGC Library</h1>
          <div className="flex items-center gap-3 text-sm text-[var(--color-muted-foreground)]">
            <span>{data.total} items</span>
            <span className="text-[var(--color-warning)]">{data.pendingCount} pending</span>
            <span className="text-[var(--color-success)]">{data.approvedCount} approved</span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Filter className="h-4 w-4 text-[var(--color-muted-foreground)] mr-1" />
          {STATUS_TABS.map((tab) => (
            <button
              type="button"
              key={tab.label}
              onClick={() => setStatusFilter(tab.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === tab.value
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {data.items.map((item) => (
            <UgcCard key={item.id} item={item} onAction={handleAction} isActioning={isActioning} />
          ))}
        </div>
      </div>

      <form
        onSubmit={handleCaptureSubmit}
        className="h-fit rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
      >
        <div className="flex items-center gap-2">
          <PlusCircle className="h-4 w-4 text-[var(--color-primary)]" />
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Capture First-Party Review</h2>
        </div>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          Manual text/image capture plus browser-recorded video reviews with teleprompter prompts.
        </p>
        <VideoReviewRecorder
          onUploaded={(upload) => {
            setCaptureForm((current) => ({
              ...current,
              mediaType: "video",
              mediaUrl: upload.assetUrl,
              sourcePostUrl: upload.assetUrl,
              primaryMediaUploadId: upload.assetId,
              caption: current.caption.trim() ? current.caption : upload.suggestedCaption,
            }));
          }}
        />
        <CaptureFields
          form={captureForm}
          captureError={captureError}
          isSubmitting={isSubmitting}
          onChange={handleCaptureChange}
        />
      </form>
    </div>
  );
}

function VideoReviewRecorder({ onUploaded }: { onUploaded: (result: VideoReviewUploadResult) => void }) {
  const [format, setFormat] = useState<SocialVideoFormat>("portrait");
  const [promptIndex, setPromptIndex] = useState(0);
  const [status, setStatus] = useState<"idle" | "recording" | "recorded" | "uploading" | "uploaded">("idle");
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const selectedFormat = SOCIAL_VIDEO_FORMATS[format];
  const selectedPrompt = TELEPROMPTER_PROMPTS[promptIndex] ?? TELEPROMPTER_PROMPTS[0]!;

  const clearPreviewUrl = useCallback(() => {
    if (previewUrlRef.current && typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = null;
    setPreviewUrl(null);
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      recorderRef.current = null;
      stopStream();
      clearPreviewUrl();
    };
  }, [clearPreviewUrl, stopStream]);

  const startRecording = async () => {
    setError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Browser recording is unavailable in this environment.");
      return;
    }

    try {
      clearPreviewUrl();
      setRecordedBlob(null);
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      const mimeType = chooseVideoMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "video/webm" });
        stopStream();
        if (blob.size === 0) {
          setError("Recording produced no video data.");
          setStatus("idle");
          return;
        }

        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setRecordedBlob(blob);
        setStatus("recorded");
      };

      recorder.start();
      setStatus("recording");
    } catch {
      stopStream();
      setError("Camera or microphone permission was denied.");
      setStatus("idle");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
  };

  const uploadRecording = async () => {
    if (!recordedBlob) {
      setError("Record a video before uploading.");
      return;
    }

    setError(null);
    setStatus("uploading");
    try {
      const formData = new FormData();
      const file = new File(
        [recordedBlob],
        `video-review-${selectedFormat.fileSuffix}.webm`,
        { type: recordedBlob.type || "video/webm" },
      );
      formData.append("file", file);

      const response = await apiFetch<{ data: PageAssetUploadResponse }>("/crm/pages/assets", {
        method: "POST",
        body: formData,
      });
      const assetUrl = resolveUploadedAssetUrl(response.data);
      onUploaded({
        assetId: response.data.id,
        assetUrl,
        suggestedCaption: selectedPrompt.caption,
      });
      setStatus("uploaded");
    } catch {
      setStatus("recorded");
      setError("Unable to upload the recorded video asset.");
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">Video review recorder</h3>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Record in-browser, upload to tenant assets, then save as a video UGC item.
          </p>
        </div>
        <Video className="h-4 w-4 text-[var(--color-primary)]" />
      </div>

      <div className="mt-3 grid gap-2">
        <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
          Social format
          <select
            value={format}
            onChange={(event) => setFormat(event.target.value as SocialVideoFormat)}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {Object.entries(SOCIAL_VIDEO_FORMATS).map(([value, config]) => (
              <option key={value} value={value}>{config.label} - {config.description}</option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
          Teleprompter script
          <select
            value={promptIndex}
            onChange={(event) => setPromptIndex(Number(event.target.value))}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {TELEPROMPTER_PROMPTS.map((prompt, index) => (
              <option key={prompt.title} value={index}>{prompt.title}</option>
            ))}
          </select>
        </label>

        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <p className="text-xs font-medium text-[var(--color-foreground)]">{selectedPrompt.title}</p>
          <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs text-[var(--color-muted-foreground)]">
            {selectedPrompt.lines.map((line) => <li key={line}>{line}</li>)}
          </ol>
        </div>

        <div className={`mx-auto w-full max-w-40 overflow-hidden rounded-lg border border-[var(--color-border)] bg-black ${selectedFormat.aspectClass}`}>
          {previewUrl ? (
            <video src={previewUrl} controls className="h-full w-full object-cover" aria-label="Recorded video review preview" />
          ) : (
            <div className="flex h-full items-center justify-center px-3 text-center text-[10px] text-white/70">
              {selectedFormat.label} preview
            </div>
          )}
        </div>

        {error ? (
          <div className="rounded-md border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-3 py-2 text-xs text-[var(--color-error)]">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={startRecording}
            disabled={status === "recording" || status === "uploading"}
            className="rounded-md border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-60"
          >
            Start video review recording
          </button>
          <button
            type="button"
            onClick={stopRecording}
            disabled={status !== "recording"}
            className="rounded-md border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-60"
          >
            Stop recording
          </button>
          <button
            type="button"
            onClick={uploadRecording}
            disabled={!recordedBlob || status === "recording" || status === "uploading"}
            className="rounded-md bg-[var(--color-primary)] px-3 py-2 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            {status === "uploading" ? "Uploading..." : status === "uploaded" ? "Video uploaded" : "Upload recorded video"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CaptureFields({
  form,
  captureError,
  isSubmitting,
  onChange,
}: {
  form: CaptureFormState;
  captureError: string | null;
  isSubmitting: boolean;
  onChange: <K extends keyof CaptureFormState>(field: K, value: CaptureFormState[K]) => void;
}) {
  return (
    <div className="mt-4 grid gap-3">
      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Platform
        <input
          value={form.platform}
          onChange={(event) => onChange("platform", event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
      </label>

      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Author Name
        <input
          value={form.authorName}
          onChange={(event) => onChange("authorName", event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          required
        />
      </label>

      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Author Handle
        <input
          value={form.authorHandle}
          onChange={(event) => onChange("authorHandle", event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          required
        />
      </label>

      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Media Type
        <select
          value={form.mediaType}
          onChange={(event) => onChange("mediaType", event.target.value as CaptureMediaType)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          <option value="text">Text</option>
          <option value="image">Image</option>
          <option value="video">Video</option>
        </select>
      </label>

      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Caption
        <textarea
          value={form.caption}
          onChange={(event) => onChange("caption", event.target.value)}
          className="min-h-24 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          required
        />
      </label>

      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Media URL
        <input
          value={form.mediaUrl}
          onChange={(event) => onChange("mediaUrl", event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          placeholder="Optional for text-only capture"
          required={form.mediaType !== "text"}
        />
      </label>

      {form.primaryMediaUploadId ? (
        <div className="rounded-md border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-3 py-2 text-xs text-[var(--color-success)]">
          Uploaded video asset linked: {form.primaryMediaUploadId}
        </div>
      ) : null}

      <label className="grid gap-1 text-xs text-[var(--color-muted-foreground)]">
        Source URL
        <input
          value={form.sourcePostUrl}
          onChange={(event) => onChange("sourcePostUrl", event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          required
        />
      </label>

      {captureError ? (
        <div className="rounded-md border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-3 py-2 text-xs text-[var(--color-error)]">
          {captureError}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={isSubmitting}
        className="rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
      >
        {isSubmitting ? "Saving..." : "Save First-Party Review"}
      </button>
    </div>
  );
}
